<script>
import { mapGetters } from 'vuex';
import axios from 'axios';
import { useAlert } from 'dashboard/composables';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { useAdmin } from 'dashboard/composables/useAdmin';
import Avatar from 'next/avatar/Avatar.vue';
import ContactAPI from 'dashboard/api/contacts';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import EditContact from './EditContact.vue';
import ContactMergeModal from 'dashboard/modules/contact/ContactMergeModal.vue';
import EmojiInput from 'shared/components/emoji/EmojiInput.vue';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { isTrainingConversation } from 'dashboard/helper/trainingConversation';
import { isTeamNumberContact } from 'dashboard/helper/teamNumber';
import TeamNumberBadge from 'dashboard/components/widgets/TeamNumberBadge.vue';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import ContactPdfViewer from './ContactPdfViewer.vue';

import {
  isAConversationRoute,
  isAInboxViewRoute,
  getConversationDashboardRoute,
} from '../../../../helper/routeHelpers';
import { emitter } from 'shared/helpers/mitt';

export default {
  components: {
    EditContact,
    Avatar,
    ContactMergeModal,
    EmojiInput,
    TeamNumberBadge,
    GalleryView,
    ContactPdfViewer,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
    channelType: {
      type: String,
      default: '',
    },
    inboxId: {
      type: [Number, String],
      default: null,
    },
  },
  emits: ['panelClose', 'groupMembersNavigate', 'groupUpdated'],
  setup() {
    const { isAdmin } = useAdmin();
    return {
      isAdmin,
    };
  },
  data() {
    return {
      showEditModal: false,
      showDeleteModal: false,
      isEditingName: false,
      editName: '',
      showGroupEmojiPicker: false,
      showGroupPermissionModal: false,
      showMediaDetailsModal: false,
      activeMediaTab: 'media',
      allConversationAttachments: [],
      isLoadingAllConversationMedia: false,
      hasLoadedAllConversationMedia: false,
      // Aceleres -- elemento do painel que recebe o modal de midias via Teleport.
      mediaModalTarget: null,
      galleryAttachment: null,
      showGalleryView: false,
      pdfViewerItem: null,
      pdfViewerMessage: null,
    };
  },
  computed: {
    // Aceleres -- selo "Número da Equipe" no painel de informações do contato.
    isTeamNumber() {
      return isTeamNumberContact(this.contact);
    },
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
      currentChat: 'getSelectedChat',
      selectedChatAttachments: 'getSelectedChatAttachments',
      currentUser: 'getCurrentUser',
    }),
    contactProfileLink() {
      return `/app/accounts/${this.$route.params.accountId}/contacts/${this.contact.id}`;
    },
    additionalAttributes() {
      return {
        ...(this.currentChat?.meta?.sender?.additional_attributes || {}),
        ...(this.currentChat?.meta?.sender?.custom_attributes || {}),
        ...(this.contact.additional_attributes || {}),
        ...(this.contact.custom_attributes || {}),
      };
    },
    isWhatsappGroup() {
      return (
        this.additionalAttributes.is_whatsapp_group === true ||
        this.additionalAttributes.is_whatsapp_group === 'true'
      );
    },
    whatsappGroup() {
      return this.additionalAttributes.whatsapp_group || {};
    },
    groupParticipants() {
      const participants = this.whatsappGroup.participants;
      return Array.isArray(participants) ? participants : [];
    },
    groupParticipantCount() {
      const countCandidates = [
        this.whatsappGroup.participants_count,
        this.whatsappGroup.participant_count,
        this.whatsappGroup.members_count,
        this.whatsappGroup.member_count,
        this.additionalAttributes.whatsapp_group_participants_count,
        this.additionalAttributes.whatsapp_group_members_count,
        this.additionalAttributes.participants_count,
        this.additionalAttributes.members_count,
      ];
      const count = countCandidates.find(value => {
        const numericValue = Number(value);
        return Number.isFinite(numericValue) && numericValue >= 0;
      });

      if (count !== undefined) return Number(count);

      return this.groupParticipants.length;
    },
    groupMemberLabel() {
      const suffix = this.groupParticipantCount === 1 ? 'membro' : 'membros';
      return `${this.groupParticipantCount} ${suffix}`;
    },
    groupTypeLabel() {
      return 'Grupo';
    },
    groupMetaSeparator() {
      return '·';
    },
    groupNameLabel() {
      return 'Nome do grupo';
    },
    addGroupEmojiLabel() {
      return 'Adicionar emoji ao nome do grupo';
    },
    saveGroupNameLabel() {
      return 'Salvar nome do grupo';
    },
    editGroupNameLabel() {
      return 'Editar nome do grupo';
    },
    groupPermissionInfoLabel() {
      return 'Regras para edição do nome do grupo';
    },
    groupPermissionMessage() {
      return 'Somente admins podem editar os dados deste grupo';
    },
    groupPermissionButtonLabel() {
      return 'OK';
    },
    canEditGroupName() {
      const groupAdminFlag = [
        this.whatsappGroup.current_user_is_admin,
        this.whatsappGroup.currentUserIsAdmin,
        this.whatsappGroup.is_current_user_admin,
      ].find(value => value !== undefined);

      if (groupAdminFlag !== undefined) {
        return groupAdminFlag === true || groupAdminFlag === 'true';
      }

      const currentUserIdentifiers = [
        this.currentUser?.id,
        this.currentUser?.phone_number,
        this.currentUser?.phoneNumber,
        this.currentUser?.identifier,
        this.currentUser?.custom_attributes?.phone_number,
        this.currentUser?.additional_attributes?.phone_number,
      ]
        .map(identifier => this.normalizeGroupIdentifier(identifier))
        .filter(Boolean);

      const currentParticipant = this.groupParticipants.find(participant => {
        if (!participant || typeof participant === 'string') return false;
        if (participant.is_me || participant.isMe) return true;

        const participantIdentifiers = [
          participant.id,
          participant.user_id,
          participant.phone_number,
          participant.phoneNumber,
          participant.identifier,
          participant.jid,
        ]
          .map(identifier => this.normalizeGroupIdentifier(identifier))
          .filter(Boolean);

        return participantIdentifiers.some(identifier =>
          currentUserIdentifiers.includes(identifier)
        );
      });

      return Boolean(
        currentParticipant &&
          (currentParticipant.is_owner ||
            currentParticipant.is_admin ||
            currentParticipant.admin ||
            ['owner', 'creator', 'admin'].includes(currentParticipant.role))
      );
    },
    location() {
      const {
        country = '',
        city = '',
        country_code: countryCode,
      } = this.additionalAttributes;
      const cityAndCountry = [city, country].filter(item => !!item).join(', ');

      if (!cityAndCountry) {
        return '';
      }
      return this.findCountryFlag(countryCode, cityAndCountry);
    },
    socialProfiles() {
      const {
        social_profiles: socialProfiles,
        screen_name: twitterScreenName,
        social_telegram_user_name: telegramUsername,
      } = this.additionalAttributes;

      const telegram = socialProfiles?.telegram || telegramUsername || '';
      const twitter = socialProfiles?.twitter || twitterScreenName || '';

      return {
        ...(socialProfiles || {}),
        twitter,
        telegram,
      };
    },
    isWhatsappConversation() {
      return (
        this.channelType === 'Channel::Whatsapp' ||
        this.channelType === 'Channel::Api'
      );
    },
    isTrainingContactContext() {
      const senderId = Number(this.currentChat?.meta?.sender?.id || 0);
      const contactId = Number(this.contact?.id || 0);

      return (
        contactId > 0 &&
        senderId === contactId &&
        isTrainingConversation(this.currentChat, this.currentUser)
      );
    },
    // Delete Modal
    confirmDeleteMessage() {
      return ` ${this.contact.name}?`;
    },
    contactDisplayName() {
      return this.contact.name || this.contact.phone_number || 'Contato';
    },
    contactDisplayPhone() {
      return (
        this.contact.phone_number ||
        this.contact.phoneNumber ||
        this.contact.identifier ||
        ''
      );
    },
    shouldShowContactDisplayPhone() {
      if (this.isWhatsappGroup || !this.contactDisplayPhone) return false;

      const normalize = value =>
        String(value || '')
          .replace(/^group:/, '')
          .replace(/\D/g, '');
      return (
        normalize(this.contactDisplayName) !==
        normalize(this.contactDisplayPhone)
      );
    },
    mediaLinksDocsTitle() {
      return 'Mídia, links e docs';
    },
    emailActionTitle() {
      return 'E-mail';
    },
    // Aceleres -- áudios não fazem parte de "Mídia, links e docs": não possuem
    // thumbnail e ocupavam slots da galeria renderizando <img> quebrado.
    visibleChatAttachments() {
      return (this.selectedChatAttachments || []).filter(
        attachment => !this.isAudioAttachment(attachment)
      );
    },
    chatMediaAttachments() {
      return this.visibleChatAttachments.filter(attachment =>
        this.isPreviewableMedia(attachment)
      );
    },
    chatDocumentAttachments() {
      return this.visibleChatAttachments.filter(
        attachment => !this.isPreviewableMedia(attachment)
      );
    },
    chatLinks() {
      const messages = this.currentChat?.messages || [];
      const urls = new Set();

      messages.forEach(message => {
        const content = message.content || '';
        const matches = content.match(/https?:\/\/[^\s<>"']+/g) || [];
        matches.forEach(url => urls.add(url));
      });

      return Array.from(urls);
    },
    mediaLinksDocsCount() {
      return (
        this.chatMediaAttachments.length +
        this.chatDocumentAttachments.length +
        this.chatLinks.length
      );
    },
    mediaPreviewItems() {
      return this.chatMediaAttachments.slice(0, 4).map((attachment, index) => ({
        id: `media-${attachment.id || index}`,
        type: 'media',
        kind: this.isVideoAttachment(attachment) ? 'video' : 'image',
        title: this.attachmentFileName(attachment, 'Mídia'),
        url: attachment.data_url || attachment.dataUrl || '',
        thumbUrl: this.attachmentThumbUrl(attachment),
        attachment,
      }));
    },
    mediaDetailsAttachments() {
      const attachments = this.hasLoadedAllConversationMedia
        ? this.allConversationAttachments
        : this.selectedChatAttachments || [];

      return attachments.filter(
        attachment => !this.isAudioAttachment(attachment)
      );
    },
    mediaDetailItems() {
      return this.mediaDetailsAttachments
        .filter(attachment => this.isPreviewableMedia(attachment))
        .map((attachment, index) => this.toMediaDetailItem(attachment, index));
    },
    documentDetailItems() {
      return this.mediaDetailsAttachments
        .filter(attachment => !this.isPreviewableMedia(attachment))
        .map((attachment, index) => this.toDocumentDetailItem(attachment, index));
    },
    linkDetailItems() {
      return this.chatLinks.map((url, index) => ({
        id: `link-${index}`,
        title: url,
        url,
      }));
    },
    activeMediaTabItems() {
      if (this.activeMediaTab === 'documents') {
        return this.documentDetailItems;
      }

      if (this.activeMediaTab === 'links') {
        return this.linkDetailItems;
      }

      return this.mediaDetailItems;
    },
    // Aceleres -- so as midias da conversa aberta podem ir para o GalleryView.
    // Ele resolve a mensagem dentro de getSelectedChat e, quando nao acha, cai
    // no conversation_id da conversa atual -- responder/excluir/fixar acabariam
    // agindo na conversa errada para midias vindas de "todas as conversas".
    galleryAttachments() {
      return this.visibleChatAttachments.filter(attachment =>
        this.isPreviewableMedia(attachment)
      );
    },
    // Aceleres -- galeria e visualizador de PDF nascem fora do painel e ficam
    // abaixo dele no empilhamento; ambos precisam do mesmo rebaixamento.
    hasOverlayOpen() {
      return this.showGalleryView || Boolean(this.pdfViewerMessage);
    },
    mediaDetailsEmptyMessage() {
      const labels = {
        media: 'Nenhuma mídia enviada nesta conversa.',
        documents: 'Nenhum documento enviado nesta conversa.',
        links: 'Nenhum link enviado nesta conversa.',
      };

      return labels[this.activeMediaTab];
    },
  },
  watch: {
    'contact.id': {
      handler(id) {
        this.$store.dispatch('contacts/fetchContactableInbox', id);
      },
      immediate: true,
    },
    hasOverlayOpen(isOpen) {
      this.syncOverlayPanelStacking(isOpen);
    },
  },
  // Aceleres -- o modal precisa cobrir o painel inteiro (corpo, list-group e
  // footer-actions). Ancorar no viewport nunca fecha com a caixa do painel, que
  // e posicionada a partir da area da conversa. Entao ele e teleportado para o
  // proprio container do painel e preenchido com inset: 0. Sem o container
  // (layout inesperado) cai no body com o posicionamento antigo.
  mounted() {
    this.mediaModalTarget =
      this.$el?.closest?.('.contact-details-modal-container') || null;
  },
  beforeUnmount() {
    this.syncOverlayPanelStacking(false);
  },
  methods: {
    dynamicTime,
    toggleEditModal() {
      this.showEditModal = !this.showEditModal;
    },
    closeEditModal() {
      this.showEditModal = false;
    },
    openAceleresNewMessage() {
      const phone = this.contact.phone_number || this.contact.phoneNumber;
      if (!phone) {
        useAlert('Este contato nao tem telefone cadastrado.');
        return;
      }

      emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
      this.$router.push({
        name: 'home',
        params: { accountId: this.$route.params.accountId },
        query: { start_phone: phone },
      });
    },
    openEmail() {
      if (!this.contact.email) {
        useAlert('Este contato nao tem e-mail cadastrado.');
        return;
      }

      window.location.href = `mailto:${this.contact.email}`;
    },
    openPhoneCall() {
      const phone = this.contact.phone_number || this.contact.phoneNumber;
      if (!phone) {
        useAlert('Este contato nao tem telefone cadastrado.');
        return;
      }

      window.location.href = `tel:${phone}`;
    },
    toggleDeleteModal() {
      this.showDeleteModal = !this.showDeleteModal;
    },
    confirmDeletion() {
      if (this.isTrainingContactContext) return;

      this.deleteContact(this.contact);
      this.closeDelete();
    },
    closeDelete() {
      this.showDeleteModal = false;
      this.showEditModal = false;
    },
    findCountryFlag(countryCode, cityAndCountry) {
      try {
        if (!countryCode) {
          return `${cityAndCountry} 🌎`;
        }

        const code = countryCode?.toLowerCase();
        return `${cityAndCountry} <span class="fi fi-${code} size-3.5"></span>`;
      } catch (error) {
        return '';
      }
    },
    async deleteContact({ id }) {
      try {
        await this.$store.dispatch('contacts/delete', id);
        this.$emit('panelClose');
        useAlert(this.$t('DELETE_CONTACT.API.SUCCESS_MESSAGE'));

        if (isAConversationRoute(this.$route.name)) {
          this.$router.push({
            name: getConversationDashboardRoute(this.$route.name),
          });
        } else if (isAInboxViewRoute(this.$route.name)) {
          this.$router.push({
            name: 'inbox_view',
          });
        } else if (this.$route.name !== 'contacts_dashboard') {
          this.$router.push({
            name: 'contacts_dashboard',
          });
        }
      } catch (error) {
        useAlert(
          error.message
            ? error.message
            : this.$t('DELETE_CONTACT.API.ERROR_MESSAGE')
        );
      }
    },
    openMergeModal() {
      this.$refs.mergeModal?.open();
    },
    startEditingName() {
      if (!this.canEditGroupName) return;
      this.editName = this.contact.name || '';
      this.isEditingName = true;
      this.showGroupEmojiPicker = false;
      this.$nextTick(() => {
        this.$refs.nameInput?.focus();
      });
    },
    async saveNameEdit() {
      if (!this.isEditingName) return;
      this.showGroupEmojiPicker = false;
      this.isEditingName = false;
      const trimmed = this.editName.trim();
      if (trimmed && trimmed !== this.contact.name) {
        if (this.isWhatsappGroup) {
          try {
            const response = await axios.patch(
              `${this.wameGroupApiUrl()}/${this.inboxId}`,
              { group_jid: this.whatsappGroup.jid, name: trimmed },
              { headers: this.wameGroupHeaders() }
            );
            this.$emit('groupUpdated', response.data.group);
            useAlert('Nome do grupo atualizado no WhatsApp.');
          } catch (error) {
            useAlert(
              error.response?.data?.message ||
                'Não foi possível atualizar o nome do grupo.'
            );
          }
          return;
        }
        this.updateContactField({ name: trimmed });
      }
    },
    cancelNameEdit() {
      this.showGroupEmojiPicker = false;
      this.isEditingName = false;
    },
    toggleGroupEmojiPicker() {
      this.showGroupEmojiPicker = !this.showGroupEmojiPicker;
    },
    appendGroupEmoji(emoji) {
      if (!emoji) return;
      this.editName = `${this.editName}${emoji}`;
      this.showGroupEmojiPicker = false;
      this.$nextTick(() => this.$refs.nameInput?.focus());
    },
    hideGroupEmojiPicker() {
      this.showGroupEmojiPicker = false;
    },
    normalizeGroupIdentifier(identifier) {
      return String(identifier || '')
        .replace(/^group:/, '')
        .replace(/@.*/, '')
        .replace(/\D/g, '');
    },
    openGroupPermissionModal() {
      this.showGroupPermissionModal = true;
    },
    closeGroupPermissionModal() {
      this.showGroupPermissionModal = false;
    },
    wameGroupApiUrl() {
      return 'https://homolog.aceleres.ai/api/wame/groups';
    },
    wameGroupHeaders() {
      return {
        'X-Chatwoot-Token': this.currentUser?.access_token || '',
        'X-Chatwoot-Account-Id': this.$route.params.accountId || '',
      };
    },
    isAudioAttachment(attachment) {
      const fileType = attachment?.file_type || attachment?.fileType || '';
      const contentType =
        attachment?.content_type || attachment?.contentType || '';
      return /^audio/i.test(fileType) || /^audio\//i.test(contentType);
    },
    isImageAttachment(attachment) {
      const fileType = attachment?.file_type || attachment?.fileType || '';
      const contentType =
        attachment?.content_type || attachment?.contentType || '';
      return /^image$/i.test(fileType) || /^image\//i.test(contentType);
    },
    isVideoAttachment(attachment) {
      const fileType = attachment?.file_type || attachment?.fileType || '';
      const contentType =
        attachment?.content_type || attachment?.contentType || '';
      return (
        /^(video|ig_reel)/i.test(fileType) || /^video\//i.test(contentType)
      );
    },
    isPreviewableMedia(attachment) {
      if (this.isAudioAttachment(attachment)) return false;

      const fileType = attachment?.file_type || attachment?.fileType || '';
      return /image|video|ig_reel/i.test(fileType);
    },
    // Aceleres -- sem thumbnail gerado, só imagem pode ir direto para o <img>.
    // Vídeo/áudio caíam em <img src="arquivo.mp4"> e quebravam o bloco.
    attachmentThumbUrl(attachment) {
      const thumbUrl = attachment?.thumb_url || attachment?.thumbUrl || '';
      if (thumbUrl) return thumbUrl;

      return this.isImageAttachment(attachment)
        ? attachment?.data_url || attachment?.dataUrl || ''
        : '';
    },
    // Aceleres -- os previewers do ActiveStorage estão desligados
    // (config/application.rb), então vídeo nunca tem thumb_url. Assim como o
    // balão da conversa (Video.vue), deixamos o próprio <video> pintar o quadro.
    seekVideoPreviewFrame(event) {
      const video = event?.target;
      if (!video || video.currentTime > 0) return;

      // Primeiro quadro costuma ser preto; avança um pouco quando dá.
      if (Number.isFinite(video.duration) && video.duration > 0.6) {
        video.currentTime = 0.5;
      }
    },
    // Aceleres -- anexo externo (ig_reel/story) nao tem blob e continua sem
    // file_name mesmo depois do fix no jbuilder. A URL do ActiveStorage termina
    // no nome do arquivo, entao ela serve de reserva antes do rotulo generico.
    attachmentFileName(attachment, fallback) {
      const fileName = attachment?.file_name || attachment?.filename || '';
      if (fileName) return fileName;

      const url = attachment?.data_url || attachment?.dataUrl || '';
      const lastSegment = url.split('?')[0].split('/').pop() || '';

      try {
        const decoded = decodeURIComponent(lastSegment);
        if (/\.[a-z0-9]{2,5}$/i.test(decoded)) return decoded;
      } catch (error) {
        // URL com escape invalido -- mantem o rotulo generico.
      }

      return fallback;
    },
    toMediaDetailItem(attachment, index) {
      return {
        id: `media-${attachment.id || attachment.message_id || index}`,
        kind: this.isVideoAttachment(attachment) ? 'video' : 'image',
        title: this.attachmentFileName(attachment, 'Mídia'),
        url: attachment.data_url || attachment.dataUrl || '',
        thumbUrl: this.attachmentThumbUrl(attachment),
        attachment,
      };
    },
    toDocumentDetailItem(attachment, index) {
      return {
        id: `document-${attachment.id || attachment.message_id || index}`,
        type: 'document',
        title: this.attachmentFileName(attachment, 'Documento'),
        url: attachment.data_url || attachment.dataUrl || '',
        extension: attachment.extension || '',
        contentType: attachment.content_type || attachment.contentType || '',
        attachment,
      };
    },
    // Aceleres -- mesma deteccao do balao do chat (File.vue): content_type
    // quando existe, senao a extensao, senao o final do nome do arquivo.
    isPdfItem(item) {
      const contentType = (item?.contentType || '').toLowerCase();
      if (contentType === 'application/pdf') return true;

      const extension = String(item?.extension || '')
        .replace('.', '')
        .toLowerCase();
      if (extension) return extension === 'pdf';

      return /\.pdf$/i.test(item?.title || '');
    },
    // Aceleres -- o visualizador de PDF precisa da mensagem real para os botoes
    // de acao (reagir, destacar, fixar). Ela so existe na store da conversa
    // aberta, entao PDF de outra conversa nao tem como abrir o modal.
    findMessageForItem(item) {
      const messageId = item?.attachment?.message_id || item?.messageId;
      if (!messageId) return null;

      return (
        (this.currentChat?.messages || []).find(
          message => String(message.id) === String(messageId)
        ) || null
      );
    },
    openPdfViewerItem(item, message) {
      this.pdfViewerItem = item;
      this.pdfViewerMessage = message;
    },
    closePdfViewer() {
      this.pdfViewerItem = null;
      this.pdfViewerMessage = null;
    },
    // Aceleres -- documento nao-PDF baixa em vez de abrir aba, como no chat.
    // O atributo download so vale para mesma origem; se um dia a URL apontar
    // para outro host o navegador navega, que e exatamente o que ja acontecia.
    downloadDocumentItem(item) {
      const link = document.createElement('a');
      link.href = item.url;
      link.download = item.title || '';
      link.rel = 'noreferrer noopener nofollow';
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    openMediaDetailsModal() {
      this.setMediaDetailsTab('media');
    },
    closeMediaDetailsModal() {
      this.showMediaDetailsModal = false;
    },
    setMediaDetailsTab(tab) {
      this.activeMediaTab = tab;
      this.showMediaDetailsModal = true;
    },
    async loadAllConversationMedia() {
      if (
        this.isLoadingAllConversationMedia ||
        this.hasLoadedAllConversationMedia ||
        !this.contact?.id
      ) {
        return;
      }

      this.isLoadingAllConversationMedia = true;
      try {
        const { data } = await ContactAPI.getConversations(this.contact.id);
        const conversations = data?.payload || [];
        const responses = await Promise.all(
          conversations.map(conversation =>
            ConversationAPI.getAllAttachments(conversation.id)
          )
        );

        this.allConversationAttachments = responses.flatMap(
          (response, conversationIndex) =>
            (response.data?.payload || []).map((attachment, attachmentIndex) => ({
              ...attachment,
              id:
                attachment.id ||
                `conversation-${conversationIndex}-attachment-${attachmentIndex}`,
            }))
        );
        this.hasLoadedAllConversationMedia = true;
      } catch (error) {
        useAlert('Não foi possível carregar as mídias de todas as conversas.');
      } finally {
        this.isLoadingAllConversationMedia = false;
      }
    },
    isCurrentConversationAttachment(attachment) {
      const url = attachment?.data_url || attachment?.dataUrl || '';
      if (!url) return false;

      return this.galleryAttachments.some(
        item => (item.data_url || item.dataUrl) === url
      );
    },
    openMediaPreviewItem(item) {
      // Aceleres -- imagem/video da conversa aberta vao para o GalleryView, o
      // mesmo visualizador dos baloes do chat. Documento, link e midia de outra
      // conversa seguem abrindo em nova guia.
      if (
        item?.attachment &&
        this.isCurrentConversationAttachment(item.attachment)
      ) {
        this.galleryAttachment = item.attachment;
        this.showGalleryView = true;
        return;
      }

      if (!item?.url) return;

      if (item.type === 'document') {
        if (this.isPdfItem(item)) {
          // Sem a mensagem na store os botoes de acao agiriam sobre dados
          // vazios; nesse caso o PDF baixa, como os demais documentos.
          const message = this.findMessageForItem(item);
          if (message) {
            this.openPdfViewerItem(item, message);
            return;
          }
        }

        this.downloadDocumentItem(item);
        return;
      }

      window.open(item.url, '_blank', 'noopener,noreferrer');
    },
    closeGalleryView() {
      this.showGalleryView = false;
      this.galleryAttachment = null;
    },
    // Aceleres -- o overlay do painel esta no teto do z-index (2147483647) e os
    // visualizadores vivem dentro de .conversation-details-wrap com 2147483500,
    // entao ficariam atras do painel. Rebaixamos o painel enquanto um deles
    // estiver aberto, em vez de fecha-lo, para o usuario voltar na aba de midia.
    syncOverlayPanelStacking(isOpen) {
      document.body?.classList?.toggle('acl-contact-overlay-open', !!isOpen);
    },
    onFieldUpdate(field, value) {
      this.updateContactField({ [field]: value });
    },
    async updateContactField(attrs) {
      const contactId = this.contact.id;
      try {
        await this.$store.dispatch('contacts/update', {
          id: contactId,
          ...attrs,
        });
        useAlert(this.$t('CONTACT_FORM.SUCCESS_MESSAGE'));
        await this.$store.dispatch('contacts/fetchContactableInbox', contactId);
      } catch (error) {
        if (error instanceof DuplicateContactException) {
          const detail = error.contactErrorDetail;
          if (detail) {
            useAlert(detail);
          } else {
            const invalidAttrs = Array.isArray(error.data) ? error.data : [];
            if (invalidAttrs.includes('email')) {
              useAlert(this.$t('CONTACT_FORM.FORM.EMAIL_ADDRESS.DUPLICATE'));
            } else if (invalidAttrs.includes('phone_number')) {
              useAlert(this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.DUPLICATE'));
            } else {
              useAlert(this.$t('CONTACT_FORM.ERROR_MESSAGE'));
            }
          }
        } else if (error instanceof ExceptionWithMessage) {
          useAlert(error.data);
        } else {
          useAlert(error.message || this.$t('CONTACT_FORM.ERROR_MESSAGE'));
        }
      }
    },
  },
};
</script>

<template>
  <div
    class="contact-details-info relative box-border w-full max-w-full overflow-x-hidden !px-4 !pb-4 !pt-4 md:!px-8 md:!pt-6"
  >
    <div class="flex flex-col w-full gap-4 text-left rtl:text-right">
      <section class="contact-details-info__hero">
        <Avatar
          v-if="showAvatar"
          :src="contact.thumbnail"
          :name="contactDisplayName"
          :status="contact.availability_status"
          :size="120"
          hide-offline-status
          rounded-full
          class="contact-details-info__avatar !size-24 md:!size-[120px]"
        />

        <div
          v-if="isWhatsappGroup && isEditingName"
          class="contact-details-info__name-edit"
        >
          <input
            ref="nameInput"
            v-model="editName"
            type="text"
            maxlength="255"
            class="contact-details-info__name-input"
            :aria-label="groupNameLabel"
            @keydown.enter.prevent="saveNameEdit"
            @keydown.esc.prevent="cancelNameEdit"
          />
          <button
            type="button"
            class="contact-details-info__name-edit-action contact-details-info__name-edit-action--reaction"
            :aria-label="addGroupEmojiLabel"
            @click="toggleGroupEmojiPicker"
          >
            <span class="i-lucide-smile-plus" />
          </button>
          <EmojiInput
            v-if="showGroupEmojiPicker"
            v-on-clickaway="hideGroupEmojiPicker"
            class="contact-details-info__emoji-picker"
            :on-click="appendGroupEmoji"
            scroll-sync-tabs
          />
          <button
            type="button"
            class="contact-details-info__name-edit-action contact-details-info__name-edit-action--confirm"
            :aria-label="saveGroupNameLabel"
            @click="saveNameEdit"
          >
            <span class="i-lucide-check" />
          </button>
        </div>
        <div v-else class="contact-details-info__name-row">
          <h3 class="contact-details-info__name !text-xl md:!text-2xl">
            {{ contactDisplayName }}
          </h3>
          <button
            v-if="isWhatsappGroup && canEditGroupName"
            v-tooltip.top="editGroupNameLabel"
            type="button"
            class="contact-details-info__name-edit-trigger"
            :aria-label="editGroupNameLabel"
            @click="startEditingName"
          >
            <span class="i-lucide-pencil" />
          </button>
          <button
            v-else-if="isWhatsappGroup"
            v-tooltip.top="groupPermissionInfoLabel"
            type="button"
            class="contact-details-info__name-edit-trigger"
            :aria-label="groupPermissionInfoLabel"
            @click="openGroupPermissionModal"
          >
            <span class="i-lucide-info" />
          </button>
        </div>

        <div v-if="isTeamNumber" class="contact-details-info__team-number">
          <TeamNumberBadge />
        </div>

        <p v-if="isWhatsappGroup" class="contact-details-info__group-meta">
          <span>{{ groupTypeLabel }}</span>
          <span aria-hidden="true">{{ groupMetaSeparator }}</span>
          <button
            type="button"
            class="contact-details-info__group-members"
            @click="$emit('groupMembersNavigate')"
          >
            {{ groupMemberLabel }}
          </button>
        </p>

        <p
          v-if="shouldShowContactDisplayPhone"
          class="contact-details-info__phone"
        >
          {{ contactDisplayPhone }}
        </p>

        <div
          class="contact-details-info__actions !grid !w-full !grid-cols-3 !gap-1 md:!flex md:!w-auto md:!gap-[14px]"
        >
          <button
            type="button"
            class="contact-details-info__action !min-w-0 !min-h-[4.5rem] !gap-2 !px-1 !text-xs md:!min-w-[120px] md:!min-h-[92px] md:!gap-[10px] md:!px-[14px] md:!text-[0.9375rem]"
            @click="openAceleresNewMessage"
          >
            <span
              class="contact-details-info__action-icon-wrap !size-11 md:!w-[70px] md:!h-[52px]"
            >
              <span
                class="i-ph-chat-circle-dots contact-details-info__action-icon"
              />
            </span>
            <span>{{ $t('CONTACT_PANEL.NEW_MESSAGE') }}</span>
          </button>
          <button
            type="button"
            class="contact-details-info__action !min-w-0 !min-h-[4.5rem] !gap-2 !px-1 !text-xs md:!min-w-[120px] md:!min-h-[92px] md:!gap-[10px] md:!px-[14px] md:!text-[0.9375rem]"
            @click="openEmail"
          >
            <span
              class="contact-details-info__action-icon-wrap !size-11 md:!w-[70px] md:!h-[52px]"
            >
              <span class="i-lucide-mail contact-details-info__action-icon" />
            </span>
            <span>{{ emailActionTitle }}</span>
          </button>
          <button
            type="button"
            class="contact-details-info__action !min-w-0 !min-h-[4.5rem] !gap-2 !px-1 !text-xs md:!min-w-[120px] md:!min-h-[92px] md:!gap-[10px] md:!px-[14px] md:!text-[0.9375rem]"
            :disabled="uiFlags.isMerging"
            @click="openMergeModal"
          >
            <span
              class="contact-details-info__action-icon-wrap !size-11 md:!w-[70px] md:!h-[52px]"
            >
              <span
                class="i-ph-arrows-merge contact-details-info__action-icon"
              />
            </span>
            <span>{{ $t('CONTACT_PANEL.MERGE_CONTACT') }}</span>
          </button>
        </div>
      </section>

      <section
        v-if="!isWhatsappGroup"
        class="contact-details-media"
        :aria-label="mediaLinksDocsTitle"
      >
        <button
          type="button"
          class="contact-details-media__header"
          @click="openMediaDetailsModal"
        >
          <span class="contact-details-media__title-wrap">
            <span class="i-lucide-images contact-details-media__title-icon" />
            <span class="contact-details-media__title">
              {{ mediaLinksDocsTitle }}
            </span>
          </span>
          <span class="contact-details-media__count">
            {{ mediaLinksDocsCount }}
          </span>
        </button>
        <div class="contact-details-media__preview-list">
          <button
            v-for="item in mediaPreviewItems"
            :key="item.id"
            type="button"
            class="contact-details-media__preview"
            :class="`contact-details-media__preview--${item.type}`"
            :title="item.title"
            @click="openMediaPreviewItem(item)"
          >
            <img
              v-if="item.thumbUrl"
              :src="item.thumbUrl"
              :alt="item.title"
              class="contact-details-media__preview-image"
            />
            <video
              v-else-if="item.kind === 'video' && item.url"
              :src="item.url"
              class="contact-details-media__preview-image contact-details-media__preview-video"
              muted
              playsinline
              preload="metadata"
              @loadedmetadata="seekVideoPreviewFrame"
            />
            <span v-else class="contact-details-media__preview-placeholder">
              <span
                class="contact-details-media__preview-icon"
                :class="
                  item.type === 'link' ? 'i-lucide-link' : 'i-lucide-video'
                "
              />
            </span>
            <span
              v-if="item.kind === 'video' && item.url"
              class="contact-details-media__preview-play"
            >
              <span class="i-lucide-play" />
            </span>
          </button>
        </div>
      </section>

      <div class="flex flex-col items-start gap-1.5 min-w-0 w-full">
        <p v-if="additionalAttributes.description" class="break-words mb-0.5">
          {{ additionalAttributes.description }}
        </p>
        <div
          v-if="isWhatsappGroup"
          class="flex flex-col w-full gap-2 text-sm text-n-slate-11"
        >
          <span v-if="whatsappGroup.description" class="break-words">
            {{ whatsappGroup.description }}
          </span>
        </div>
      </div>
      <EditContact
        v-if="showEditModal"
        :show="showEditModal"
        :contact="contact"
        :can-delete="isAdmin && !isTrainingContactContext"
        @cancel="closeEditModal"
        @delete="toggleDeleteModal"
      />
      <ContactMergeModal ref="mergeModal" :primary-contact="contact" />
    </div>
    <GalleryView
      v-if="galleryAttachment"
      v-model:show="showGalleryView"
      :attachment="galleryAttachment"
      :all-attachments="galleryAttachments"
      @close="closeGalleryView"
    />
    <Teleport v-if="pdfViewerMessage" to=".conversation-details-wrap">
      <ContactPdfViewer
        :message="pdfViewerMessage"
        :url="pdfViewerItem.url"
        :file-name="pdfViewerItem.title"
        @close="closePdfViewer"
      />
    </Teleport>
    <Teleport to="body">
      <div v-if="showDeleteModal" class="contact-info-delete-modal-layer">
        <woot-delete-modal
          v-model:show="showDeleteModal"
          :on-close="closeDelete"
          :on-confirm="confirmDeletion"
          :title="$t('DELETE_CONTACT.CONFIRM.TITLE')"
          :message="$t('DELETE_CONTACT.CONFIRM.MESSAGE')"
          :message-value="confirmDeleteMessage"
          :confirm-text="$t('DELETE_CONTACT.CONFIRM.YES')"
          :reject-text="$t('DELETE_CONTACT.CONFIRM.NO')"
        />
      </div>
    </Teleport>
    <Teleport :to="mediaModalTarget || 'body'">
      <section
        v-if="showMediaDetailsModal"
        class="contact-details-media-modal"
        :class="{
          'contact-details-media-modal--detached': !mediaModalTarget,
        }"
        aria-label="Mídias, documentos e links"
        role="dialog"
        aria-modal="true"
        tabindex="-1"
        @keydown.esc="closeMediaDetailsModal"
      >
        <header class="contact-details-media-modal__header">
          <button
            v-tooltip.top="'Voltar para os dados do contato'"
            type="button"
            class="contact-details-media-modal__back"
            aria-label="Voltar para os dados do contato"
            @click.stop="closeMediaDetailsModal"
          >
            <span class="i-lucide-arrow-left" />
          </button>
        </header>
        <div class="contact-details-media-modal__tabs" role="tablist">
          <button
            type="button"
            role="tab"
            :aria-selected="activeMediaTab === 'media'"
            class="contact-details-media-modal__tab"
            :class="{ 'contact-details-media-modal__tab--active': activeMediaTab === 'media' }"
            @click.stop.prevent="setMediaDetailsTab('media')"
          >
            Mídia
          </button>
          <button
            type="button"
            role="tab"
            :aria-selected="activeMediaTab === 'documents'"
            class="contact-details-media-modal__tab"
            :class="{ 'contact-details-media-modal__tab--active': activeMediaTab === 'documents' }"
            @click.stop.prevent="setMediaDetailsTab('documents')"
          >
            Documentos
          </button>
          <button
            type="button"
            role="tab"
            :aria-selected="activeMediaTab === 'links'"
            class="contact-details-media-modal__tab"
            :class="{ 'contact-details-media-modal__tab--active': activeMediaTab === 'links' }"
            @click.stop.prevent="setMediaDetailsTab('links')"
          >
            Links
          </button>
        </div>
        <div class="contact-details-media-modal__body">
          <p class="contact-details-media-modal__section-title">Neste mês</p>
          <div
            v-if="activeMediaTab === 'media' && activeMediaTabItems.length"
            class="contact-details-media-modal__media-grid"
          >
            <button
              v-for="item in activeMediaTabItems"
              :key="item.id"
              type="button"
              class="contact-details-media-modal__media-card"
              :title="item.title"
              @click="openMediaPreviewItem(item)"
            >
              <img
                v-if="item.thumbUrl"
                :src="item.thumbUrl"
                :alt="item.title"
                class="contact-details-media-modal__media-image"
              />
              <video
                v-else-if="item.kind === 'video' && item.url"
                :src="item.url"
                class="contact-details-media-modal__media-image contact-details-media-modal__media-video"
                muted
                playsinline
                preload="metadata"
                @loadedmetadata="seekVideoPreviewFrame"
              />
              <span v-else class="i-lucide-image-off contact-details-media-modal__fallback-icon" />
              <span
                v-if="item.kind === 'video' && item.url"
                class="contact-details-media-modal__media-play"
              >
                <span class="i-lucide-play" />
              </span>
            </button>
          </div>
          <div
            v-else-if="activeMediaTab === 'documents' && activeMediaTabItems.length"
            class="contact-details-media-modal__list"
          >
            <button
              v-for="item in activeMediaTabItems"
              :key="item.id"
              type="button"
              class="contact-details-media-modal__list-item"
              @click="openMediaPreviewItem(item)"
            >
              <span class="i-lucide-file-text contact-details-media-modal__list-icon" />
              <span class="contact-details-media-modal__list-text">{{ item.title }}</span>
              <span class="contact-details-media-modal__file-extension">{{ item.extension }}</span>
            </button>
          </div>
          <div
            v-else-if="activeMediaTab === 'links' && activeMediaTabItems.length"
            class="contact-details-media-modal__list"
          >
            <button
              v-for="item in activeMediaTabItems"
              :key="item.id"
              type="button"
              class="contact-details-media-modal__list-item"
              @click="openMediaPreviewItem(item)"
            >
              <span class="i-lucide-link contact-details-media-modal__list-icon" />
              <span class="contact-details-media-modal__list-text">{{ item.title }}</span>
            </button>
          </div>
          <p v-else class="contact-details-media-modal__empty">
            {{ mediaDetailsEmptyMessage }}
          </p>
        </div>
        <footer class="contact-details-media-modal__footer">
          <button
            type="button"
            class="contact-details-media-modal__all-media"
            :disabled="isLoadingAllConversationMedia || hasLoadedAllConversationMedia"
            @click="loadAllConversationMedia"
          >
            <span class="i-lucide-images" />
            <span>
              {{
                hasLoadedAllConversationMedia
                  ? 'Mídias de todas as conversas carregadas'
                  : isLoadingAllConversationMedia
                    ? 'Carregando mídias...'
                    : 'Ver mídias de todas as conversas'
              }}
            </span>
          </button>
        </footer>
      </section>
    </Teleport>
    <woot-modal
      v-model:show="showGroupPermissionModal"
      :on-close="closeGroupPermissionModal"
      :show-close-button="false"
      class="group-permission-modal"
    >
      <div class="group-permission-modal__content">
        <p class="group-permission-modal__message">
          {{ groupPermissionMessage }}
        </p>
        <button
          type="button"
          class="group-permission-modal__confirm"
          @click="closeGroupPermissionModal"
        >
          {{ groupPermissionButtonLabel }}
        </button>
      </div>
    </woot-modal>
  </div>
</template>

<style scoped>
.contact-details-info {
  background: #ffffff;
}

.contact-info-delete-modal-layer {
  position: relative;
  z-index: 10000;
}

.contact-info-delete-modal-layer :deep(.modal-mask) {
  z-index: 10000 !important;
}

.contact-details-info__hero {
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 10px 0 18px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.28);
  text-align: center;
}

.contact-details-info__avatar {
  width: 120px !important;
  height: 120px !important;
  flex-shrink: 0;
}

.contact-details-info__name {
  max-width: min(100%, 520px);
  margin: 6px 0 0;
  overflow-wrap: anywhere;
  color: #111827;
  font-size: 1.625rem;
  font-weight: 700;
  line-height: 1.2;
}

.contact-details-info__name-row,
.contact-details-info__name-edit {
  position: relative;
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.contact-details-info__name-row .contact-details-info__name {
  max-width: calc(100% - 48px);
  font-size: 1.5rem;
}

.contact-details-info__name-edit {
  gap: 8px;
  justify-content: flex-start;
}

.contact-details-info__name-input {
  width: min(100%, 360px);
  min-width: 0;
  height: 42px;
  padding: 0 12px;
  border: 0 !important;
  border-radius: 0;
  background: transparent !important;
  color: #111827;
  font-size: 1.625rem;
  font-weight: 500;
  line-height: 1.2;
  outline: none !important;
  text-align: left;
}

.contact-details-info__name-input:focus {
  border: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  outline: none !important;
}

.contact-details-info__name-input:hover {
  border: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  outline: none !important;
}

.contact-details-info__name-edit-trigger,
.contact-details-info__name-edit-action {
  display: inline-flex;
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 9999px;
  background: transparent;
  color: #000;
  cursor: pointer;
}

.contact-details-info__name-edit-trigger:hover,
.contact-details-info__name-edit-trigger:focus-visible,
.contact-details-info__name-edit-action:hover,
.contact-details-info__name-edit-action:focus-visible {
  background: rgba(148, 163, 184, 0.16);
  outline: none;
}

.contact-details-info__name-edit-trigger {
  position: absolute;
  right: 0;
  color: #000;
}

.contact-details-info__name-edit-trigger > span,
.contact-details-info__name-edit-action > span {
  width: 25px;
  height: 25px;
  font-size: 25px;
}

.contact-details-info__group-meta {
  font-size: 1.05rem;
}

.contact-details-info__name-edit-action--confirm {
  color: #000;
}

.contact-details-info__name-edit-action--reaction:hover,
.contact-details-info__name-edit-action--reaction:focus-visible {
  background: transparent;
}

.contact-details-info__emoji-picker {
  top: 52px;
  right: 40px;
}

.contact-details-info__emoji-picker.emoji-dialog {
  top: -95px !important;
  right: 0 !important;
  width: 22rem !important;
  height: 30rem !important;
  overflow: visible !important;
  border-radius: 1.5rem !important;
  background: #fff !important;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.18) !important;
  outline: 1px solid #e2e8f0 !important;
}

.contact-details-info__emoji-picker.emoji-dialog::before {
  top: -0.75rem !important;
  right: 2rem !important;
  bottom: auto !important;
  width: 1.5rem !important;
  height: 0.75rem !important;
  transform: rotate(180deg);
  background: #fff !important;
}

.contact-details-info__emoji-picker :deep(.emoji-dialog--footer) {
  order: -1;
  flex-shrink: 0;
  border-bottom: 1px solid #edf2f7;
  border-radius: 1.5rem 1.5rem 0 0;
  background: #fff !important;
  padding: 0.5rem 0.75rem 0 !important;
}

.contact-details-info__emoji-picker :deep(.emoji-dialog--footer ul) {
  justify-content: space-between;
  gap: 0.25rem;
  padding: 0 !important;
}

.contact-details-info__emoji-picker :deep(.emoji-dialog--footer ul > li) {
  flex: 1;
  padding: 0 !important;
}

.contact-details-info__emoji-picker :deep(.emoji-dialog--footer .emoji--item) {
  width: 100%;
  height: 3.25rem;
  justify-content: center;
  border-radius: 0.75rem 0.75rem 0 0;
  font-size: 1.5rem;
}

.contact-details-info__emoji-picker
  :deep(.emoji-dialog--footer .emoji--item.active) {
  position: relative;
  background: transparent !important;
  color: #18b566;
}

.contact-details-info__emoji-picker
  :deep(.emoji-dialog--footer .emoji--item.active::after) {
  position: absolute;
  right: 0.5rem;
  bottom: 0;
  left: 0.5rem;
  height: 0.25rem;
  border-radius: 9999px 9999px 0 0;
  background: #18b566;
  content: '';
}

.contact-details-info__emoji-picker :deep(.emoji-dialog > .flex) {
  overflow: hidden;
  border-radius: inherit;
}

.contact-details-info__emoji-picker
  :deep(.emoji-dialog > .flex > div:first-child) {
  margin: 0.75rem 1rem !important;
}

.contact-details-info__emoji-picker
  :deep(.emoji-dialog > .flex > div:first-child input) {
  height: 2.75rem !important;
  border: 2px solid #18b566 !important;
  border-radius: 9999px !important;
  background: #fff !important;
  color: #334155;
  font-size: 1rem !important;
  padding-left: 3rem !important;
}

.contact-details-info__emoji-picker :deep(.emoji-item) {
  height: 22rem !important;
  padding: 0 0.75rem 1rem;
}

.contact-details-info__emoji-picker :deep(.emoji--section-title) {
  padding: 0.75rem 0.5rem 0.5rem !important;
  color: #334155 !important;
  font-size: 1rem !important;
  font-weight: 700 !important;
}

.contact-details-info__emoji-picker :deep(.emoji--row) {
  display: grid;
  grid-template-columns: repeat(8, minmax(0, 1fr));
  gap: 0.25rem;
  padding: 0.25rem 0.5rem !important;
}

.contact-details-info__emoji-picker :deep(.emoji--row .emoji--item) {
  width: 2rem;
  height: 2rem;
  margin: 0 !important;
  padding: 0 !important;
  border-radius: 0.5rem;
  font-size: 1.5rem;
}

.group-permission-modal__content {
  display: flex;
  width: 100%;
  min-height: auto;
  flex-direction: column;
  gap: 34px;
  padding: 36px 40px 32px;
}

.group-permission-modal__message {
  margin: 0;
  color: #111827;
  font-size: 1.05rem;
  line-height: 1.35;
  white-space: nowrap;
}

.group-permission-modal__confirm {
  align-self: flex-end;
  min-width: 96px;
  min-height: 42px;
  padding: 0 20px;
  border: 0;
  border-radius: 9999px;
  background: #18b566;
  color: #fff;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 700;
}

.group-permission-modal__confirm:hover,
.group-permission-modal__confirm:focus-visible {
  background: #129653;
  outline: none;
}

.contact-details-info__phone {
  margin: 0;
  color: #64748b;
  font-size: 0.95rem;
  font-weight: 500;
}

:global(.modal-mask.group-permission-modal) {
  background: rgba(15, 23, 42, 0.55) !important;
  backdrop-filter: none !important;
}

:global(.modal-mask.group-permission-modal .modal-container) {
  width: min(34rem, calc(100vw - 2rem)) !important;
  max-width: none !important;
  overflow: hidden !important;
  border-radius: 2rem !important;
  background: #fff !important;
}

.contact-details-info__group-meta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: -2px 0 0;
  color: #64748b;
  font-size: 1.05rem;
  font-weight: 500;
}

.contact-details-info__group-members {
  border: 0;
  background: transparent;
  color: #22c55e;
  cursor: pointer;
  font: inherit;
  font-weight: 600;
  padding: 0;
  text-decoration: none;
}

.contact-details-info__group-members:hover,
.contact-details-info__group-members:focus-visible {
  color: #16a34a;
  outline: none;
  text-decoration: underline;
}

.contact-details-info__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 14px;
  padding-top: 10px;
}

.contact-details-info__action {
  display: inline-flex;
  min-width: 120px;
  min-height: 92px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 0 14px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #111827;
  font-size: 0.9375rem;
  font-weight: 600;
  box-shadow: none;
  transition: color 0.16s ease;
}

.contact-details-info__action:hover,
.contact-details-info__action:focus-visible {
  outline: none;
}

.contact-details-info__action-icon-wrap {
  display: inline-flex;
  width: 70px;
  height: 52px;
  align-items: center;
  justify-content: center;
  border-radius: 50px;
  background: rgba(194, 189, 184, 0.15);
  transition: background 0.16s ease;
}

.contact-details-info__action:hover .contact-details-info__action-icon-wrap,
.contact-details-info__action:focus-visible
  .contact-details-info__action-icon-wrap {
  background: rgba(194, 189, 184, 0.28);
}

.contact-details-info__action-icon {
  width: 25px;
  height: 25px;
  color: #000000;
}

.contact-details-media {
  width: 100%;
  margin-top: -2px;
}

.contact-details-media__header {
  display: flex;
  width: 100%;
  min-height: 58px;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 18px;
  border: 0;
  border-radius: 16px;
  background: rgba(194, 189, 184, 0.15);
  color: #111827;
  cursor: pointer;
}

.contact-details-media__title-wrap {
  display: inline-flex;
  min-width: 0;
  align-items: center;
  gap: 14px;
}

.contact-details-media__title-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  color: #5f6368;
}

.contact-details-media__title {
  min-width: 0;
  overflow: hidden;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.contact-details-media__count {
  flex-shrink: 0;
  color: #5f6368;
  font-size: 1rem;
  font-weight: 500;
}

.contact-details-media__preview-list {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  padding: 12px 0 0;
}

.contact-details-media__preview {
  position: relative;
  aspect-ratio: 1.12 / 1;
  overflow: hidden;
  border: 0;
  border-radius: 9px;
  background: #f3f4f6;
  cursor: pointer;
}

.contact-details-media__preview-image {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Aceleres -- GalleryView e PdfConversationViewer sao absolute inset-0 dentro
   de .conversation-details-wrap com z-index 2147483500, e o overlay do painel
   usa 2147483647. Enquanto um deles estiver aberto rebaixamos o painel para ele
   aparecer por cima; a classe dobrada vence o !important do escopo da
   ConversationSidebar. */
:global(
    body.acl-contact-overlay-open
      .contact-details-modal-overlay.contact-details-modal-overlay
  ) {
  z-index: 2147483400 !important;
}

/* Aceleres -- inset: 0 sobre o .contact-details-modal-container faz o modal
   cobrir o painel inteiro por construcao, incluindo o footer-actions. */
.contact-details-media-modal {
  position: absolute;
  inset: 0;
  z-index: 2147483647;
  display: flex;
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
  background: #ffffff;
  color: #111827;
  outline: none;
}

/* Reserva: sem o container do painel, mantem o comportamento antigo. */
.contact-details-media-modal--detached {
  position: fixed;
  inset: 0 0 auto auto;
  width: min(100vw, 987px);
  height: 100vh;
}

.contact-details-media-modal__header {
  display: flex;
  min-height: 76px;
  align-items: center;
  padding: 18px 32px 12px;
}

.contact-details-media-modal__back {
  display: inline-flex;
  width: 48px;
  height: 48px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #111827;
  cursor: pointer;
}

.contact-details-media-modal__back:hover,
.contact-details-media-modal__back:focus-visible {
  background: rgba(17, 24, 39, 0.06);
  outline: none;
}

.contact-details-media-modal__back > span {
  width: 20px;
  height: 20px;
  font-size: 24px;
}

.contact-details-media-modal__tabs {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  border-bottom: 1px solid #d1d5db;
  padding: 0 24px;
}

.contact-details-media-modal__tab {
  position: relative;
  min-height: 58px;
  border: 0;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  font-size: 1.125rem;
  font-weight: 500;
}

.contact-details-media-modal__tab--active {
  color: #111827;
  font-weight: 700;
}

.contact-details-media-modal__tab--active::after {
  position: absolute;
  right: 0;
  bottom: -1px;
  left: 0;
  height: 2px;
  border-radius: 0;
  background: #18b566;
  content: '';
}

.contact-details-media-modal__body {
  flex: 1;
  overflow-y: auto;
  padding: 42px 48px 112px;
}

.contact-details-media-modal__section-title {
  margin: 0 0 18px;
  color: #374151;
  font-size: 1rem;
  font-weight: 700;
  text-transform: uppercase;
}

.contact-details-media-modal__media-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.contact-details-media-modal__media-card {
  position: relative;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  border: 0;
  border-radius: 6px;
  background: #f3f4f6;
  cursor: pointer;
}

.contact-details-media-modal__media-image {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.contact-details-media-modal__fallback-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 32px;
  height: 32px;
  color: #6b7280;
  transform: translate(-50%, -50%);
}

/* Aceleres -- miniatura de vídeo: o próprio <video> pinta o quadro, e o clique
   precisa chegar no <button> que abre o arquivo. */
.contact-details-media__preview-video,
.contact-details-media-modal__media-video {
  background: #000;
  pointer-events: none;
}

.contact-details-media__preview-play,
.contact-details-media-modal__media-play {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.contact-details-media__preview-play > span,
.contact-details-media-modal__media-play > span {
  width: 20px;
  height: 20px;
  color: #fff;
  filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.65));
}

.contact-details-media-modal__media-play > span {
  width: 26px;
  height: 26px;
}

.contact-details-media-modal__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.contact-details-media-modal__list-item {
  display: flex;
  width: 100%;
  min-height: 64px;
  align-items: center;
  gap: 14px;
  padding: 12px 14px;
  border: 0;
  border-radius: 10px;
  background: #f8fafc;
  color: #111827;
  cursor: pointer;
  text-align: left;
}

.contact-details-media-modal__list-item:hover,
.contact-details-media-modal__list-item:focus-visible {
  background: #f1f5f9;
  outline: none;
}

.contact-details-media-modal__list-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  color: #18b566;
}

.contact-details-media-modal__list-text {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  font-size: 0.9375rem;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.contact-details-media-modal__file-extension {
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
}

.contact-details-media-modal__empty {
  margin: 28px 0;
  color: #6b7280;
  font-size: 0.9375rem;
  text-align: center;
}

.contact-details-media-modal__footer {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  min-height: 92px;
  padding: 14px 48px 22px;
  border-top: 1px solid #e5e7eb;
  background: #ffffff;
}

.contact-details-media-modal__all-media {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  border: 0;
  background: transparent;
  color: #18a85f;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 700;
}

.contact-details-media-modal__all-media:disabled {
  cursor: default;
  opacity: 0.65;
}

.contact-details-media-modal__all-media > span:first-child {
  width: 26px;
  height: 26px;
}

.contact-details-media__preview-placeholder {
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
}

.contact-details-media__preview-icon {
  width: 26px;
  height: 26px;
  color: #5f6368;
}

.contact-details-group {
  position: fixed;
  top: 0;
  right: 0;
  z-index: 2147483647;
  display: flex;
  width: min(100vw, 987px);
  height: 100vh;
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
  background: #ffffff;
  color: #111827;
}

.contact-details-group__header {
  display: flex;
  min-height: 76px;
  align-items: center;
  gap: 22px;
  padding: 18px 32px 12px;
}

.contact-details-group__close {
  display: inline-flex;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #111827;
  cursor: pointer;
  font-size: 28px;
}

.contact-details-group__close:hover,
.contact-details-group__close:focus-visible {
  background: rgba(17, 24, 39, 0.06);
  outline: none;
}

.contact-details-group__close > span {
  width: 28px;
  height: 28px;
}

.contact-details-group__title {
  margin: 0;
  color: #1f2937;
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.25;
}

.contact-details-group__content {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 26px;
  overflow-y: auto;
  padding: 20px 48px 42px;
}

.contact-details-group__actions {
  display: flex;
  flex-direction: column;
  gap: 26px;
  padding: 8px 0 12px;
}

.contact-details-group__action-row {
  display: flex;
  min-height: 74px;
  align-items: center;
  gap: 26px;
  padding: 0;
  border-radius: 0;
  background: transparent;
  color: #1f2937;
  font-size: 1.125rem;
  font-weight: 600;
}

.contact-details-group__action-icon {
  display: inline-flex;
  width: 64px;
  height: 64px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #18b566;
  color: #ffffff;
  font-size: 24px;
}

.contact-details-group__action-icon > span {
  width: 26px;
  height: 26px;
}

.contact-details-group__members {
  display: flex;
  flex-direction: column;
  gap: 0;
  padding-top: 0;
}

.contact-details-group__member {
  display: flex;
  min-height: 96px;
  align-items: center;
  gap: 20px;
  padding: 12px 0;
}

.contact-details-group__member-avatar {
  flex-shrink: 0;
}

.contact-details-group__member-text {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 6px;
}

.contact-details-group__member-name {
  overflow: hidden;
  color: #1f2937;
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.contact-details-group__member-phone {
  overflow: hidden;
  color: #7a7f86;
  font-size: 0.9375rem;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.contact-details-group__member-role {
  flex-shrink: 0;
  max-width: 150px;
  overflow: hidden;
  padding: 6px 10px;
  border-radius: 6px;
  background: #d9fbdc;
  color: #18864b;
  font-size: 0.8125rem;
  font-weight: 700;
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.contact-details-group__empty {
  margin: 16px 0 0;
  padding: 18px 12px;
  border-radius: 10px;
  background: #f8fafc;
  color: #64748b;
  font-size: 0.875rem;
  text-align: center;
}

@media (max-width: 767px) {
  .contact-details-info {
    box-sizing: border-box;
    max-width: 100%;
    overflow-x: hidden;
  }

  .contact-details-info__actions,
  .contact-details-media,
  .contact-details-media__header {
    max-width: 100%;
  }

  .contact-details-info__action {
    min-width: 0;
    max-width: 100%;
    overflow-wrap: anywhere;
  }

  .contact-details-info__action > span:last-child {
    max-width: 100%;
    overflow-wrap: anywhere;
    text-align: center;
  }

  .group-permission-modal__message {
    white-space: normal;
    overflow-wrap: anywhere;
  }

  /* Aceleres -- no modo acoplado o inset: 0 da base ja cobre o painel; so a
     reserva desacoplada precisa das medidas de viewport. */
  .contact-details-media-modal--detached {
    inset: 0;
    width: 100vw;
    height: 100dvh;
  }

  .contact-details-media-modal__header {
    min-height: 68px;
    padding: 14px 20px 10px;
  }

  .contact-details-media-modal__tabs {
    padding: 0 8px;
  }

  .contact-details-media-modal__tab {
    min-height: 52px;
    font-size: 0.9375rem;
  }

  .contact-details-media-modal__body {
    padding: 28px 20px 104px;
  }

  .contact-details-media-modal__footer {
    min-height: 84px;
    padding: 14px 20px 18px;
  }

  .contact-details-group {
    inset: 0 !important;
    width: 100vw !important;
    height: 100dvh !important;
  }

  .contact-details-group__header {
    min-height: 68px;
    gap: 16px;
    padding: 14px 20px 10px;
  }

  .contact-details-group__content {
    gap: 20px;
    padding: 16px 22px 28px;
  }

  .contact-details-group__action-row {
    min-height: 64px;
    gap: 18px;
    font-size: 1rem;
  }

  .contact-details-group__action-icon {
    width: 54px;
    height: 54px;
    font-size: 21px;
  }

  .contact-details-group__member {
    min-height: 82px;
    gap: 14px;
  }

  .contact-details-group__member-name {
    font-size: 1rem;
  }

  .contact-details-group__member-phone {
    font-size: 0.8125rem;
  }

  .contact-details-group__member-role {
    max-width: 112px;
    font-size: 0.6875rem;
  }
}
</style>
